/**
 * Google Search Console connector.
 *
 * Runs entirely in the buyer's browser. The access token is decrypted from
 * IndexedDB by the caller and passed in. We never log or persist tokens.
 *
 * API surface used:
 *   GET  https://searchconsole.googleapis.com/webmasters/v3/sites
 *   POST https://searchconsole.googleapis.com/webmasters/v3/sites/{siteUrl}/searchAnalytics/query
 *
 * Required OAuth scope: https://www.googleapis.com/auth/webmasters.readonly
 */

import type {
  GSCSite,
  GSCReportData,
  GSCDailyRow,
  GSCQueryRow,
} from "@agencylens/shared";

const API_BASE = "https://searchconsole.googleapis.com/webmasters/v3";

export class GSCError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "GSCError";
  }
}

/* ----------------------------------------------------- listSites */

interface ApiSiteEntry {
  siteUrl?: string;
  permissionLevel?: string;
}

interface ApiSitesResponse {
  siteEntry?: ApiSiteEntry[];
}

export async function listSites(accessToken: string): Promise<GSCSite[]> {
  const res = await fetch(`${API_BASE}/sites`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new GSCError(
      `GSC listSites failed: ${res.status} ${res.statusText} ${body.slice(0, 200)}`,
      res.status,
    );
  }
  const data = (await res.json()) as ApiSitesResponse;
  return (data.siteEntry ?? [])
    .filter((s): s is { siteUrl: string; permissionLevel: string } =>
      Boolean(s.siteUrl) && Boolean(s.permissionLevel),
    )
    // Hide siteUnverifiedUser — GSC returns a 403 on data queries for these
    // and they confuse the picker UI.
    .filter((s) => s.permissionLevel !== "siteUnverifiedUser")
    .map((s) => ({
      siteUrl: s.siteUrl,
      permissionLevel: s.permissionLevel,
    }));
}

/* ----------------------------------------------------- fetchReport */

interface ApiQueryRow {
  keys?: string[];
  clicks?: number;
  impressions?: number;
  ctr?: number;
  position?: number;
}

interface ApiQueryResponse {
  rows?: ApiQueryRow[];
  responseAggregationType?: string;
}

async function runQuery(
  accessToken: string,
  siteUrl: string,
  body: Record<string, unknown>,
): Promise<ApiQueryRow[]> {
  const encoded = encodeURIComponent(siteUrl);
  const url = `${API_BASE}/sites/${encoded}/searchAnalytics/query`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new GSCError(
      `GSC searchAnalytics.query failed: ${res.status} ${res.statusText} ${text.slice(0, 300)}`,
      res.status,
    );
  }
  const json = (await res.json()) as ApiQueryResponse;
  return json.rows ?? [];
}

export async function fetchReport(
  accessToken: string,
  site: GSCSite,
  startDate: string,
  endDate: string,
): Promise<GSCReportData> {
  const dailyBody = {
    startDate,
    endDate,
    dimensions: ["date"],
    rowLimit: 1000,
  };
  const queriesBody = {
    startDate,
    endDate,
    dimensions: ["query"],
    rowLimit: 25,
  };

  const [dailyRows, queryRows] = await Promise.all([
    runQuery(accessToken, site.siteUrl, dailyBody),
    runQuery(accessToken, site.siteUrl, queriesBody),
  ]);

  const daily: GSCDailyRow[] = dailyRows.map((r) => ({
    date: r.keys?.[0] ?? "",
    clicks: r.clicks ?? 0,
    impressions: r.impressions ?? 0,
    ctr: r.ctr ?? 0,
    position: r.position ?? 0,
  }));

  // GSC returns dimensions sorted however the user asked; default order from
  // the API is by clicks desc for queries but we sort defensively.
  const topQueries: GSCQueryRow[] = queryRows
    .map((r) => ({
      query: r.keys?.[0] ?? "",
      clicks: r.clicks ?? 0,
      impressions: r.impressions ?? 0,
      ctr: r.ctr ?? 0,
      position: r.position ?? 0,
    }))
    .sort((a, b) => b.clicks - a.clicks);

  const totalClicks = daily.reduce((s, r) => s + r.clicks, 0);
  const totalImpressions = daily.reduce((s, r) => s + r.impressions, 0);
  const totals = {
    clicks: totalClicks,
    impressions: totalImpressions,
    ctr: totalImpressions > 0 ? totalClicks / totalImpressions : 0,
    position:
      daily.length > 0
        ? daily.reduce((s, r) => s + r.position, 0) / daily.length
        : 0,
  };

  return {
    site,
    startDate,
    endDate,
    daily,
    topQueries,
    totals,
    fetchedAt: new Date().toISOString(),
  };
}

/* ----------------------------------------------------- date helpers */

export function isoDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * GSC data is typically usable from yesterday backwards; today + last 1-2 days
 * lag. We default to a 30-day window ending 3 days ago so reports don't show
 * sudden drops from unreported recent data.
 */
export function defaultDateRange(days = 30, lagDays = 3): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  const end = new Date(now);
  end.setUTCDate(end.getUTCDate() - lagDays);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return { startDate: isoDate(start), endDate: isoDate(end) };
}

export const GSC_SCOPE = "https://www.googleapis.com/auth/webmasters.readonly";
